import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const FaqContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 20px;
  font-family: 'Inter', 'Poppins', sans-serif;
  color: #1a202c;
  background: linear-gradient(to bottom, #f9fafb 0%, #ffffff 100%);
`;

const FaqTitle = styled.h1`
  text-align: center;
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 20px;
  color: #2d3748;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(to right, #4c51bf, #667eea);
    border-radius: 2px;
  }
`;

const FaqSubtitle = styled.p`
  text-align: center;
  font-size: 1.2rem;
  color: #718096;
  margin-bottom: 60px;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const FaqSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 40px;
  animation: ${fadeIn} 0.5s ease-out;
`;

const SectionColumn = styled.div`
  flex: 1;
  min-width: 450px;
  background: white;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 1024px) {
    min-width: 100%;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 2px solid #e2e8f0;
  color: #2d3748;
  display: flex;
  align-items: center;
  
  &::before {
    content: '';
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: linear-gradient(to right, #4c51bf, #667eea);
    margin-right: 12px;
  }
`;

const AccordionItem = styled.div`
  border-bottom: 1px solid #edf2f7;
  padding: 20px 0;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #f8fafc;
    border-radius: 8px;
    padding-left: 10px;
    padding-right: 10px;
  }
  
  &:first-child {
    border-top: 1px solid #edf2f7;
  }
`;

const AccordionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  cursor: pointer;
  font-size: 1.1rem;
  font-weight: 600;
  line-height: 1.5;
  color: #2d3748;
  transition: color 0.2s ease-in-out;

  &:hover {
    color: #4c51bf;
  }
`;

const AccordionQuestion = styled.span`
  flex: 1;
  padding-right: 20px;
  text-align: left;
`;

const AccordionIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${props => props.isOpen ? 'linear-gradient(to right, #4c51bf, #667eea)' : '#edf2f7'};
  color: ${props => props.isOpen ? 'white' : '#4c51bf'};
  font-size: 1.2rem;
  font-weight: bold;
  transform: ${props => (props.isOpen ? 'rotate(45deg)' : 'rotate(0)')};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
`;

const AccordionContent = styled.div`
  max-height: ${props => (props.isOpen ? '1000px' : '0')};
  overflow: hidden;
  transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), padding 0.3s ease;
  padding-top: ${props => (props.isOpen ? '18px' : '0')};
  font-size: 1rem;
  line-height: 1.7;
  color: #4a5568;
  text-align: left;
  border-left: ${props => props.isOpen ? '3px solid #c3dafe' : '0'};
  padding-left: ${props => props.isOpen ? '15px' : '0'};
`;

const ContactPrompt = styled.div`
  text-align: center;
  margin-top: 60px;
  padding: 30px;
  background: linear-gradient(to right, #f8fafc, #edf2f7);
  border-radius: 16px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 15px;
    color: #2d3748;
  }
  
  p {
    font-size: 1.1rem;
    color: #718096;
    margin-bottom: 20px;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
  
  button {
    background: linear-gradient(to right, #4c51bf, #667eea);
    color: white;
    border: none;
    padding: 12px 28px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(76, 81, 191, 0.3);
    }
  }
`;

const FaqAccordion = ({ title, items }) => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleAccordion = index => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <SectionColumn>
      <SectionTitle>{title}</SectionTitle>
      {items.map((item, index) => (
        <AccordionItem key={index}>
          <AccordionHeader onClick={() => toggleAccordion(index)}>
            <AccordionQuestion>{item.question}</AccordionQuestion>
            <AccordionIcon isOpen={openIndex === index}>+</AccordionIcon>
          </AccordionHeader>
          <AccordionContent isOpen={openIndex === index}>
            {item.answer}
          </AccordionContent>
        </AccordionItem>
      ))}
    </SectionColumn>
  );
};

const faqData = {
  specializations: {
    title: 'Specializations',
    items: [
      {
        question: 'What areas of expertise does SRK Softwares specialize in?',
        answer:
          'SRK Softwares specializes in custom software development, mobile application development, cloud solutions, and enterprise-level IT consulting.',
      },
      {
        question: 'How experienced is the team at SRK Softwares?',
        answer:
          'Our team consists of highly-skilled professionals with over a decade of experience in the software industry, delivering successful projects for a diverse range of clients.',
      },
      {
        question: 'What sets SRK Softwares expertise apart?',
        answer:
          'We stand out through our commitment to innovation, a deep understanding of emerging technologies, and a client-centric approach that ensures our solutions are perfectly tailored to your business needs.',
      },
      {
        question: 'How do you ensure the expertise is up-to-date?',
        answer:
          'Our team regularly participates in professional development, certifications, and workshops to stay abreast of the latest industry trends, tools, and best practices.',
      },
      {
        question: 'Can SRK Softwares handle projects of varying complexities?',
        answer:
          'Yes, we have a proven track record of managing projects from small-scale startups to complex, large-scale enterprise solutions, ensuring quality and scalability at every stage.',
      },
    ],
  },
  offers: {
    title: 'Offers',
    items: [
      {
        question: 'What factors determine the fees for SRK Softwares services?',
        answer:
          'Our fees are determined by project scope, complexity, technology stack, and required timelines. We provide a detailed, transparent quote after an initial consultation.',
      },
      {
        question: 'How are the contract terms structured for your services?',
        answer:
          'We offer flexible contract models, including fixed-price projects, time and material, and dedicated team arrangements, to suit your specific business and budget requirements.',
      },
      {
        question: 'Do you offer flexible payment options for your services?',
        answer:
          'Yes, we work with clients to establish a payment schedule that aligns with project milestones and their financial planning, ensuring a smooth and mutually beneficial partnership.',
      },
      {
        question: 'What happens if there are changes?',
        answer:
          'We have a well-defined change management process. Any changes to the project scope are documented, evaluated, and approved by both parties to ensure clarity and avoid disruptions.',
      },
      {
        question: 'Is there a cancellation policy in place for your services?',
        answer:
          'Our cancellation policy is outlined in our service agreement. It typically involves a notice period and covers compensation for work completed up to the date of cancellation.',
      },
    ],
  },
  outcome: {
    title: 'Outcome',
    items: [
      {
        question: 'What kind of results can I expect from SRK Softwares services?',
        answer:
          'You can expect high-quality, scalable, and secure software solutions that drive efficiency, enhance user experience, and provide a competitive edge in your market.',
      },
      {
        question:
          'How does SRK Softwares ensure the effectiveness of digital solutions?',
        answer:
          'We use agile methodologies, continuous testing, and regular client feedback loops to ensure our solutions are not only functional but also perfectly aligned with your business goals.',
      },
      {
        question:
          'Can SRK Softwares handle both small-scale and large-scale projects?',
        answer:
          'Absolutely. Our team is scalable and our processes are designed to handle projects of all sizes, ensuring the same level of quality and dedication regardless of the scale.',
      },
      {
        question: 'What metrics do you use to measure the success of your services?',
        answer:
          'We measure success using key performance indicators (KPIs) such as project completion rates, client satisfaction scores, solution performance metrics, and return on investment (ROI).',
      },
      {
        question:
          'What steps do you take to ensure that the results you provide are sustainable over the long term?',
        answer:
          'We focus on building robust, well-documented, and easily maintainable solutions. We also offer ongoing support and maintenance services to ensure long-term sustainability.',
      },
      {
        question: 'How do you adapt your strategies to changing market trends?',
        answer:
          'We maintain a forward-thinking approach, staying updated on industry shifts and technology advancements. This allows us to pivot our strategies and offer innovative solutions that keep our clients ahead of the curve.',
      },
    ],
  },
  about: {
    title: 'About SRK Softwares',
    items: [
      {
        question:
          'What is the core focus of SRK Softwares as a software development company in India?',
        answer:
          'Our core focus is to be a leading provider of innovative and reliable software solutions, helping businesses in India and worldwide achieve their digital transformation goals.',
      },
      {
        question: 'What sets SRK Softwares apart from other companies in the industry?',
        answer:
          'Our commitment to transparency, technical excellence, and a partnership-based approach with our clients distinguishes us. We do not just build software; we build long-term relationships.',
      },
      {
        question: 'Can you provide insights into SRK Softwares journey and origins?',
        answer:
          'Founded in 2010, SRK Softwares started with a small team of passionate developers. Through dedication and a focus on quality, we have grown into a respected firm serving a global clientele.',
      },
      {
        question: 'How does SRK Softwares approach innovation and adaptability?',
        answer:
          'Innovation is at the heart of our culture. We foster a continuous learning environment, encourage experimentation, and use agile methodologies to adapt quickly to new challenges and opportunities.',
      },
      {
        question:
          'What kind of projects has SRK Softwares successfully undertaken in the past?',
        answer:
          'We have a diverse portfolio, including e-commerce platforms, enterprise resource planning (ERP) systems, FinTech applications, and IoT solutions for various industries.',
      },
      {
        question:
          'What is the driving philosophy behind SRK Softwares operations?',
        answer:
          'Our philosophy is simple: empower businesses with technology. We believe in creating solutions that are not just functional but also strategically valuable, helping our clients grow and succeed.',
      },
    ],
  },
};

const FAQComponent = () => {
  return (
    <FaqContainer>
      <FaqTitle>Frequently Asked Questions</FaqTitle>
      <FaqSubtitle>
        Find answers to common questions about our services, expertise, and how we can help your business succeed.
      </FaqSubtitle>
      
      <FaqSection>
        <FaqAccordion 
          title={faqData.specializations.title} 
          items={faqData.specializations.items} 
        />
        <FaqAccordion 
          title={faqData.offers.title} 
          items={faqData.offers.items} 
        />
      </FaqSection>
      
      <FaqSection style={{ marginTop: '40px' }}>
        <FaqAccordion 
          title={faqData.outcome.title} 
          items={faqData.outcome.items} 
        />
        <FaqAccordion 
          title={faqData.about.title} 
          items={faqData.about.items} 
        />
      </FaqSection>
      
      <ContactPrompt>
        <h3>Still have questions?</h3>
        <p>Can't find the answer you're looking for? Please reach out to our friendly team.</p>
        <button>Contact Us</button>
      </ContactPrompt>
    </FaqContainer>
  );
};

export default FAQComponent;